/*#
# The model

Virta has one store: an **append-only log of events**. A task is never stored;
it is a *view* folded from the events that mention it. Comments, tag changes,
body edits, attachments, closes and reopens are all events, so the card's
transcript is the log rendered — there is no second structure to keep in sync.

Every event is attributed by the identity of the credential that wrote it
(`by`). Locally that is whatever identity the store was opened with; against
service-compris it will be the scoped capability token.

Tags are the whole model: bare keys (`bug`) or `key:value` (`status:doing`,
`project:tosijs-ui`). Workflow is a tag convention the tool never enforces.
*/

/*{ "parent": "Reference", "order": 10 }*/

/** Every kind of event the log accepts. Adding a kind is a model decision. */
export type EventKind =
  | 'created'
  | 'retitled'
  | 'edited'
  | 'tagged'
  | 'untagged'
  | 'commented'
  | 'retracted'
  | 'attached'
  | 'showcased'
  | 'measured'
  | 'procedure'
  | 'suggested'
  | 'practiced'
  | 'resolved'
  | 'closed'
  | 'reopened'
  | 'subscribed'
  | 'unsubscribed'
  | 'project'
  | 'reattributed'

export interface EventPayloads {
  created: { title: string; body: string; tags: string[] }
  retitled: { title: string }
  /**
   * `base` fingerprints the body this edit replaced (board #2473), so an
   * edit made from an older version is seen as concurrent: both are kept
   * and flagged, never last-write-wins. Older edits carry none.
   */
  edited: { body: string; base?: string; note?: string }
  tagged: { tag: string }
  untagged: { tag: string }
  commented: { text: string }
  /**
   * Its author takes a comment back (board #759) — or, with \`undo\`, puts it
   * back. The comment stays in the log and in the transcript, marked; a
   * retraction by anyone but the comment's author has no effect.
   */
  retracted: { commentId: string; reason: string; undo?: boolean }
  /**
   * A link (`url`), or a file stored in a storage area (`file`; board
   * #2436, service-compris 0.3.0) — then `url` is empty and the file is
   * fetched through `/blob` with the reader's own credential.
   */
  attached: { name: string; url: string; file?: AttachedFile }
  /**
   * What a finished task has to show (owner, 2026-09-28; board #2439): a
   * title, a description of at most 200 characters (bold the only
   * markdown), an image or video URL and a link. The latest one is the
   * task's; `withdrawn` takes it down.
   */
  showcased: {
    title: string
    text: string
    /** required: a showcase is something to see (owner, 2026-09-30) */
    media: string
    /** what the picture shows, for anyone who can't see it */
    alt?: string
    link: string
    /** a stored picture (board #2436) instead of a media URL */
    file?: AttachedFile
    withdrawn?: boolean
  }
  /**
   * How a practice is judged (owner, 2026-09-30; board #2472): its success
   * measure and its death condition — what result means retire or revise.
   * Replacing one is a new event; the evidence collected under the old
   * one stays tagged with it (#2476).
   */
  measured: { measure: string; death: string }
  /**
   * What a practice does when it fires (board #2474): the task must match
   * `when` (a filter), and gets the tags in `set`. A rule — checklists and
   * skills join the same modes later. Replacing it is a new version.
   */
  procedure: { when: string; set: string[] }
  /** A practice in suggest mode proposes tags for a task; a person accepts or overrules (board #2474). */
  suggested: { practice: string; trigger: string; set: string[]; text: string }
  /** A practice in automatic mode changed a task: exactly what it added and removed, so undo is one step. */
  practiced: {
    practice: string
    trigger: string
    added: string[]
    removed: string[]
  }
  /** What became of a suggestion or an automatic change: the evidence a practice's measure reads. */
  resolved: { of: string; outcome: 'accepted' | 'overruled' | 'undone' }
  closed: { reason: string }
  reopened: Record<string, never>
  subscribed: { subscriber: string }
  unsubscribed: { subscriber: string }
  /**
   * Project settings, upserted. Its `taskId` is `project:<name>`, so a
   * project has a transcript like a task does. Nothing here is required:
   * a project exists the moment a task carries `project:<name>`.
   */
  project: { name: string; settings: Partial<ProjectSettings> }
  /**
   * A correction of who a credential says it is (board #2752), appended,
   * never a rewrite: the events stamped by `token` (up to `through`, when
   * given; else from now on too), or the listed `events`, show `label` in
   * place of the token's own. Honoured only from the same credential or
   * the owner — the host's stamp on this event is the proof.
   */
  reattributed: {
    /** the credential's label to show ('' or absent: leave it) */
    label?: string
    /** the agent part of the by-line, after its `×` (board #2830) */
    agent?: string
    /** the token whose stamps this corrects */
    token?: string
    /** specific events instead of a token's range */
    events?: string[]
    /** the last moment it covers (ISO); absent covers that token's future writes too */
    through?: string
    reason?: string
  }
}

/** The pseudo task id corrections are written to: never a card. */
export const ATTRIBUTION_TASK_ID = 'virta:attribution'

/** A correction as the fold keeps it: what it says, and who stamped it. */
export interface Correction {
  id: string
  at: string
  by: string
  label?: string
  agent?: string
  token?: string
  events?: string[]
  through?: string
  reason?: string
  /** the host's stamp on the correction itself, when there is a host */
  stamp?: Attribution
}

/** What a project's tile shows and the menus borrow. Empty string = unset. */
export interface ProjectSettings {
  /** an emoji, an image URL, or an inline `data:image/` URL */
  icon: string
  /** a palette name (`sky`) or any css color */
  color: string
  /** css background: a color, a gradient, or `url(…)` */
  background: string
  description: string
  /** links */
  site: string
  /** npm package name */
  package: string
  /** GitHub `owner/repo` or a URL */
  repo: string
  /** last known version; live lookup can refresh it */
  version: string
  /** set (see `isRetired`): hidden from the scope menu, muted at the end of the overview, kept in history */
  retired: string
  /**
   * When and by whom the project adopted the board: \`2026-09-26 by Tosi ×
   * tosijs-3d\`. Written only by \`virta onboard\` run in the repo itself
   * (\`VirtaStore.enroll\`), never by the \`project\` verb — enrollment is
   * self-service, so no repo is surprised to find itself onboarded (owner,
   * 2026-09-26; board #1121). The brief speaks only for an enrolled project.
   */
  enrolled: string
  /**
   * The practices this project runs (board #2474): their ids, space-separated.
   * Opting in is the project's own choice; a practice never touches a
   * project that hasn't.
   */
  practices: string
  /**
   * Set (see `isPublic`): anyone can see the project's tile, signed in or
   * not. Counts only when a person signed in on the board set it — the
   * fold ignores a token turning it on (owner, 2026-10-13).
   */
  public: string
  /**
   * The Claude session the dispatcher pokes about this project's ready work
   * (board #161); empty is off, the default. Like \`public\`, only a person
   * signed in on the board turns it on, or changes how often and how many:
   * the fold lets a token turn it off, nothing else (each poke is a paid
   * turn; owner, 2026-09-26: virta must not surprise you by doing things).
   */
  dispatch: string
  /** minutes between pokes to the session (default 60) */
  dispatchEvery: string
  /** pokes per day, at most (default 4) */
  dispatchCap: string
}

/** The dispatch settings: a person's to change, a token's only to turn off (board #161). */
export const DISPATCH_KEYS = [
  'dispatch',
  'dispatchEvery',
  'dispatchCap',
] as const

export const PROJECT_SETTING_KEYS: (keyof ProjectSettings)[] = [
  'icon',
  'color',
  'background',
  'description',
  'site',
  'package',
  'repo',
  'version',
  'retired',
  'enrolled',
  'practices',
  'public',
  ...DISPATCH_KEYS,
]

/**
 * Whether a project is retired. Any value retires it except the spellings
 * of "not": empty, `no`, `false`, `0`, `off` — an agent that writes
 * `retired: "false"` means what it says.
 */
export function isRetired(p: Pick<ProjectSettings, 'retired'>): boolean {
  const v = String(p.retired ?? '')
    .trim()
    .toLowerCase()
  return v !== '' && !['no', 'false', '0', 'off'].includes(v)
}

/** A project as the fold knows it: settings plus who last changed them. */
export interface ProjectProfile extends ProjectSettings {
  name: string
  updatedAt: string
  updatedBy: string
}

/** The pseudo task id a project's events carry. */
export const projectEventId = (name: string): string => `project:${name}`

/** An event as it sits in the log: `seq` is the log's own ordering. */
export type VirtaEvent<K extends EventKind = EventKind> = {
  [P in K]: {
    /** Globally unique, minted by the writer. */
    id: string
    /** Position in the log. Assigned by the log on push, never by the writer. */
    seq: number
    taskId: string
    /** ISO timestamp, minted by the writer. */
    at: string
    /** The identity that wrote it, as the writer names itself (an import keeps the original author here). */
    by: string
    kind: P
    payload: EventPayloads[P]
    /**
     * Who the host says wrote it: the credential, unforgeable, stamped by the
     * endpoint (`_by`). Absent on a local log. The token's `label` is what
     * tells one agent from another, and from its human.
     */
    attribution?: Attribution
  }
}[K]

export interface Attribution {
  uid: string
  role: string
  name: string
  token?: string
  label?: string
}

/** An event before the log has assigned it a `seq`. */
export type EventDraft<K extends EventKind = EventKind> = {
  [P in K]: Omit<VirtaEvent<P>, 'seq'>
}[K]

/** The current state of a task — a fold over its events, never stored. */
export interface Task {
  id: string
  /** Stable short number, citable as `#38`. Dense over created events in log order. */
  num: number
  title: string
  body: string
  tags: string[]
  createdAt: string
  createdBy: string
  /** `at` of the latest event touching this task. */
  updatedAt: string
  /** `at` of the last change to the task's `status:` tag (creation counts). Age is measured from here. */
  statusAt: string
  closedAt: string | null
  subscribers: string[]
  comments: number
  attachments: number
  /** its showcase, when it has one (board #2439): the latest `showcased` event's */
  showcase: Showcase | null
  /** a practice's measure (board #2472): the latest `measured` event's, with its version */
  measure: Measure | null
  /** a practice's procedure (board #2474): the latest `procedure` event's, with its version */
  procedure: Procedure | null
  /** a practice's running record (board #2474): what its applications came to */
  record: PracticeRecord | null
  /** suggestions a practice made for this task that nobody has resolved yet */
  pending: PendingSuggestion[]
  /**
   * Edits made from an older version of the body, kept beside it until
   * someone chooses or merges (board #2473): never last-write-wins.
   */
  conflicts: BodyConflict[]
}

/** A concurrent edit, kept: its text, who and when. */
export interface BodyConflict {
  /** the edited event's id */
  id: string
  body: string
  by: string
  at: string
}

/**
 * A text's fingerprint (FNV-1a, 32-bit, hex): what an edit says it
 * replaced. For telling versions apart, not a secret.
 */
export function textHash(text: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0).toString(16).padStart(8, '0')
}

/** What a practice does when it fires. */
export interface Procedure {
  when: string
  set: string[]
  version: number
  at: string
}

/** A practice's record, counted as its applications resolve. */
export interface PracticeRecord extends PracticeCounts {
  /**
   * The same counts under each measure version (board #2475): evidence is
   * tagged with the measure it was collected under, so replacing a measure
   * starts a fresh count and the old one stays with its version. '0' is
   * what happened before any measure.
   */
  byMeasure: Record<string, PracticeCounts>
}

/** What a practice's applications came to. */
export interface PracticeCounts {
  suggested: number
  accepted: number
  overruled: number
  applied: number
  undone: number
}

/** A suggestion waiting on a person, as the task's card shows it. */
export interface PendingSuggestion {
  /** the suggested event's id: what accept or overrule names */
  id: string
  practice: string
  set: string[]
  text: string
}

/** How a practice is judged, as the fold keeps it. */
export interface Measure {
  measure: string
  death: string
  /** 1 for the first measure, 2 when it was replaced once… evidence carries it */
  version: number
  at: string
  by: string
}

/** A file an attachment names: where it is stored, and what the host measured. */
/** One thing a task carries, as `attachments` lists it (board #3560). */
export interface Attachment {
  name: string
  /** a stored file: fetch it from `/blob/<area>/<path>`, check its sha256 */
  file?: AttachedFile
  /** a link */
  url?: string
  at: string
  /** who attached it, as the board shows it */
  by: string
}

export interface AttachedFile {
  /** the storage area: `virta:files` */
  area: string
  /** its path in the area: `<sha256>.<ext>` — the same bytes, the same file */
  path: string
  /** its content type, one type (`image/png`) */
  type: string
  bytes: number
  sha256: string
}

/** A finished task's showcase, as the fold keeps it. */
export interface Showcase {
  title: string
  text: string
  media: string
  /** the picture's alt text: "screen capture of <title>" unless given */
  alt: string
  link: string
  /** a stored picture, fetched with the viewer's own credential (board #2436) */
  file?: AttachedFile
  /** when it was showcased: the carousel's order */
  at: string
  by: string
}

/**
 * Display weight per event kind. `salient` events are shown in the default
 * card view; `mechanical` ones fold into expandable groups. This is a view
 * decision — the agent API always gets the unfolded stream.
 */
export const SALIENCE: Record<EventKind, 'salient' | 'mechanical'> = {
  created: 'salient',
  commented: 'salient',
  retracted: 'mechanical',
  attached: 'salient',
  showcased: 'salient',
  measured: 'salient',
  procedure: 'salient',
  suggested: 'salient',
  practiced: 'salient',
  resolved: 'salient',
  closed: 'salient',
  reopened: 'salient',
  retitled: 'mechanical',
  edited: 'mechanical',
  tagged: 'mechanical',
  untagged: 'mechanical',
  subscribed: 'mechanical',
  unsubscribed: 'mechanical',
  project: 'mechanical',
  reattributed: 'salient',
}

/** Something that mints ids. Tests and the seeder use a counter; apps use random. */
export type IdSource = () => string
/** Something that tells the time as an ISO string. Tests use a fixed clock. */
export type Clock = () => string

const ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz'

/** Random short ids: 12 chars of a 32-symbol alphabet (60 bits). */
export function randomIds(prefix = ''): IdSource {
  return () => {
    const bytes = new Uint8Array(12)
    crypto.getRandomValues(bytes)
    let out = prefix
    for (const b of bytes) out += ALPHABET[b % 32]
    return out
  }
}

/** Deterministic ids: `prefix1`, `prefix2`, … */
export function counterIds(prefix = 'e'): IdSource {
  let n = 0
  return () => `${prefix}${++n}`
}

export const wallClock: Clock = () => new Date().toISOString()

/** A clock that advances a fixed number of ms per call, from a fixed start. */
export function stepClock(
  start = '2026-01-01T00:00:00.000Z',
  stepMs = 1000
): Clock {
  let t = Date.parse(start)
  return () => {
    const iso = new Date(t).toISOString()
    t += stepMs
    return iso
  }
}
